import {
  Component,
  ElementRef,
  HostListener,
  Injector,
  Renderer2,
  ViewChild,
  effect,
  inject,
  signal,
  untracked
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { ComponentBase } from '@cpd-app/shared-module/components/base';
import { CognitoAuthFacade } from '@cpd-app/state/cognito-auth';
import { AuthFacade } from '@cpd-app/state/auth';
import { FavoriteMilitaryOccupationsFacade } from '@cpd-app/state/favorite-military-occupations';
import { FavoriteOccupationsFacade } from '@cpd-app/state/favorite-occupations/favorite-occupations.facade';
import { GoalFacade } from '@cpd-app/state/goal';
import { UserProfileFacade } from '@cpd-app/state/user-profile';
import { AiChatFacade } from '@cpd-app/state/ai-chat';

import {
  AccountCreationPromptService,
  NavigationService,
  NotificationService,
  RouteStateService
} from '../../services';
import { FeedbackDialogComponent } from '../feedback-dialog/feedback-dialog.component';
import { GoalDialogComponent } from '../goal-dialog/goal-dialog.component';
import {
  computeGoalsReportLock,
  GoalsReportLock
} from './goals-report-lock.util';
import { SaveSessionDialogComponent } from '../save-session-dialog/save-session-dialog.component';
import { SavedForLaterDialogComponent } from '../saved-for-later-dialog/saved-for-later-dialog.component';
import {
  Observable,
  combineLatest,
  distinctUntilChanged,
  filter,
  firstValueFrom,
  map,
  of,
  skip,
  take,
  timeout
} from 'rxjs';
import { NAV_LINKS } from '@cpd-app/shared-module/constants';
// GR-15. Standalone, so the header can raise the same dialog the schools step
// does without pulling in the whole academic-goal feature module.
import {
  GoalKey,
  GoalSetDialogComponent,
  MilitaryOccupation,
  ServiceIntent
} from '@cpd-app/features/academic-goal/components/goal-set-dialog/goal-set-dialog.component';
import {
  GoalsForRows,
  goalRows
} from '@cpd-app/features/academic-goal/components/goal-set-dialog/goal-rows';
import { GoalCelebrationService } from '../../services/goal-celebration/goal-celebration.service';
import { GoalRemovalService } from '@cpd-app/features/goals-report/services/goal-removal.service';
import { signedInDisplay$ } from './header-signed-in';
import { SCROLL_TO } from '@cpd-app/shared-module/constants/tutorial.const';
import { STORAGE_KEY } from '@cpd-app/core-module/constants/storage-key.const';
import { SCHOOLS_EL_ID } from '@cpd-app/shared-module/constants';
import { NavigationEnd, Router } from '@angular/router';
import { ConnectSidebarService } from '../../services/connect/connect-sidebar.service';
import { JstSidebarService } from '../../services/jst/jst-sidebar.service';
import { AceSidebarService } from '../../services/ace/ace-sidebar.service';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';

/** Long enough for the dialog to be gone before the page moves under it. */
const GOAL_DIALOG_SCROLL_DELAY_MS = 300;

/**
 * Walkthrough 09-29 #9 v3, APPROVED: the STAR home navigation's controls and
 * session menu (with #11's popover and #60's Analytics row) in place of the
 * old Logout button and hamburger menu. Set to false to roll back to the old
 * controls, which stay in the template.
 */
export const USE_STAR_HEADER = true;

/** How long the phone menu waits on a gate or flag before its default. */
const RAIL_READ_TIMEOUT_MS = 1500;

@Component({
  selector: 'cpd-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss', './header-star.scss']
})
export class HeaderComponent extends ComponentBase {
  public goalCount$ = this.goalFacade.goalCount$;
  public totalGoalCount$ = this.goalFacade.totalGoalCount$;
  public favoriteOccupationCount$ =
    this.favoriteOccupationFacade.favoriteOccupationCount$;
  public isAuthenticated$ = this.cognitoAuthFacade.isAuthenticated$;
  public militaryFavoriteOccupations$ =
    this.favoriteMilitaryOccupationsFacade.favoriteMilitaryOccupations$;
  public userReclassifying$ =
    this.favoriteMilitaryOccupationsFacade.userReclassifying$;
  public hideSimilarMilitaryOccupation$ =
    this.userProfileFacade.hideSimilarMilitaryOccupation;
  /**
   * WALK-9, WCAG 2.4.3 (A). The phone Menu sheet covers the screen, so while
   * it is open it is a modal dialog: the page behind is inert and Tab stays
   * in the sheet. Every way of opening or closing it (the Menu button, a
   * click anywhere, a route change, a resize to desktop) goes through here.
   */
  public get isMenuOpen(): boolean {
    return this.phoneMenuOpen;
  }
  public set isMenuOpen(open: boolean) {
    this.phoneMenuOpen = open;
    if (open) {
      this.holdPhoneMenuModal();
    } else {
      this.releasePhoneMenuModal();
    }
  }
  private phoneMenuOpen = false;
  /** What the open sheet made inert, so closing releases only those. */
  private phoneMenuInert: HTMLElement[] = [];
  private phoneMenuKeysHeld = false;
  public isAlertsOpen = false;

  /** See USE_STAR_HEADER. */
  protected readonly starHeader = USE_STAR_HEADER;
  /** #9 v3. The desktop session menu under Account (or the menu button). */
  public readonly accountMenuOpen = signal(false);
  /**
   * The rail's panels, reached from the phone menu when the rail is hidden.
   * Looked up when pressed rather than injected up front, so the header's
   * specs need no new providers.
   */
  private readonly injector = inject(Injector);
  /** The phone menu's Connect was pressed while Connect is gated. */
  public readonly connectBlocked = signal(false);

  // 00-foundations section 2. The four destinations live in the shell so every
  // page carries them, which is what 06 acceptance criterion 1 asks for.
  public readonly navLinks = NAV_LINKS;

  // PWS 4.3.4. The bell lives in the shell, so the count is read here and the
  // drawer is rendered here; every surface gets it without asking.
  private readonly notificationService = inject(NotificationService);
  public readonly alertCount = this.notificationService.unreadCount;
  // Replaces the empty <a routerLinkActive> probe anchor this template used to
  // render; see RouteStateService.
  public readonly routeState = inject(RouteStateService);

  /**
   * ANALYTICS.18. Whether to offer the analytics dashboard in the account menu.
   *
   * Searching the whole app found ZERO links into /analytics from the header,
   * the footer or any menu. The only way in was an automatic redirect after a
   * counselor signed in, so anyone who navigated away had to know the URL.
   *
   * Uses inject() rather than a constructor parameter deliberately: this
   * component already has ten dependencies and is shared by every page, and
   * inject() keeps the change to one line that cannot reorder anything.
   */
  public readonly isCounselorOrAbove$ = inject(AuthFacade).isCounselorOrAbove$;
  /** STAFF-3. Display only; member actions keep isAuthenticated$. */
  public readonly isSignedInDisplay$ = signedInDisplay$(
    this.cognitoAuthFacade.isAuthenticated$,
    inject(AuthFacade).isStaffSignedIn$
  );
  // CPD-661 item 6: to tell "go to the occupation page" from "you are already
  // standing on it", which the router treats as nothing to do.
  private readonly router = inject(Router);
  private readonly hostElement = inject<ElementRef<HTMLElement>>(ElementRef);

  /**
   * MOBILE.1 R2. The bottom bar hides while Via is open, because the chat's
   * message box needs the bottom of the screen.
   */
  public readonly isChatViaOpen$ = inject(AiChatFacade).isOpenChatVia$;

  /** The bar's Home goes where the main navigation's Home goes. */
  public readonly homeLink = NAV_LINKS[0].path;

  @ViewChild('phoneMenuButton')
  private phoneMenuButton?: ElementRef<HTMLButtonElement>;

  /**
   * Remove in the goal dialog asks first, as it does on the occupation page
   * and the Goals Report. It wrote straight to the facade, so one press on
   * the career row deleted every goal with no way back (WCAG 3.3.4).
   */
  private readonly goalRemoval = inject(GoalRemovalService);
  @ViewChild(GoalSetDialogComponent)
  private goalSetDialog?: GoalSetDialogComponent;

  /**
   * `/home` redirects, so routerLinkActive on it is never active. The page it
   * lands on is Build Your Target, which is what "Home" means in the bar.
   */
  public get isHomeRoute(): boolean {
    const path = this.router.url.split('?')[0];
    return path === '/home' || path.startsWith('/career-planning/build-your-target');
  }

  public get isGoalsReportRoute(): boolean {
    return this.router.url.startsWith('/goals-report');
  }

  /**
   * Escape closes the phone menu and puts focus back on the Menu button.
   * Listened for in the capture phase (see holdPhoneMenuModal), so nothing on
   * the page that stops Escape can leave the sheet open.
   */
  public closePhoneMenuOnEscape(): void {
    if (!this.isMenuOpen) {
      return;
    }

    this.isMenuOpen = false;
    this.phoneMenuButton?.nativeElement.focus();
  }

  /** Escape from anywhere, and Tab wrapping inside the sheet. */
  private readonly onPhoneMenuKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Escape') {
      this.closePhoneMenuOnEscape();
      return;
    }
    if (event.key !== 'Tab') {
      return;
    }

    const sheet = document.getElementById('cpd-phone-menu');
    const items = Array.from(
      sheet?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
      ) ?? []
    );
    if (!sheet || !items.length) {
      return;
    }

    const active = document.activeElement;
    const edge = event.shiftKey ? items[0] : items[items.length - 1];
    if (active === edge || !sheet.contains(active)) {
      event.preventDefault();
      (event.shiftKey ? items[items.length - 1] : items[0]).focus();
    }
  };

  /** The page behind the sheet: the shell's main, footer and help aside. */
  private holdPhoneMenuModal(): void {
    const host = this.hostElement.nativeElement;
    if (!this.phoneMenuInert.length) {
      this.phoneMenuInert = Array.from(
        document.querySelectorAll<HTMLElement>(
          'main, footer, #site-container > aside'
        )
      ).filter((el) => !el.contains(host) && !el.hasAttribute('inert'));
      this.phoneMenuInert.forEach((el) => el.setAttribute('inert', ''));
    }
    // On window, not document: the CDK's focus monitor holds a passive
    // capture keydown listener on document, and zone.js runs every capture
    // listener there inside that one passive listener, so preventDefault
    // (which keeps Tab in the sheet) would be ignored.
    if (!this.phoneMenuKeysHeld) {
      window.addEventListener('keydown', this.onPhoneMenuKeydown, true);
      this.phoneMenuKeysHeld = true;
    }
  }

  private releasePhoneMenuModal(): void {
    this.phoneMenuInert.forEach((el) => el.removeAttribute('inert'));
    this.phoneMenuInert = [];
    if (this.phoneMenuKeysHeld) {
      window.removeEventListener('keydown', this.onPhoneMenuKeydown, true);
      this.phoneMenuKeysHeld = false;
    }
  }

  /** At 960 and up the sheet is not rendered, so it must not hold the page. */
  public override onResize(event: Event): void {
    super.onResize(event);
    if (!this.isLessThanMedium && this.isMenuOpen) {
      this.isMenuOpen = false;
    }
  }

  public override ngOnDestroy(): void {
    this.releasePhoneMenuModal();
    super.ngOnDestroy();
  }
  // BUG-590: instead of a bare disabled flag, compute WHY the Goals Report is
  // locked so the button can explain itself and route to the missing step.
  protected readonly goalsReportLock$ = combineLatest([
    this.goalFacade.isCareerGoalSet$,
    this.userProfileFacade.userProfile$
  ]).pipe(
    map(([isCareerGoalSet, userProfile]) =>
      computeGoalsReportLock(!!isCareerGoalSet, userProfile)
    )
  );
  constructor(
    private favoriteOccupationFacade: FavoriteOccupationsFacade,
    private goalFacade: GoalFacade,
    // GR-15. Owns whether the goal dialog is showing, for every surface that
    // raises it.
    private goalCelebration: GoalCelebrationService,
    private dialog: MatDialog,
    private userProfileFacade: UserProfileFacade,
    private navigationService: NavigationService,
    private favoriteMilitaryOccupationsFacade: FavoriteMilitaryOccupationsFacade,
    private renderer: Renderer2,
    private cognitoAuthFacade: CognitoAuthFacade,
    private accountCreationPromptService: AccountCreationPromptService
  ) {
    super();

    this.renderer.listen('window', 'click', (e: Event) => {
      if (this.isMenuOpen) {
        this.isMenuOpen = false;
      }
    });

    // A link in the sheet, or anything else, moved the page: the sheet goes.
    this.subscribedTo = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        if (this.isMenuOpen) {
          this.isMenuOpen = false;
        }
      });

    /*
     * The count the bell exists to show has to arrive before the drawer is
     * opened, otherwise nothing ever tells a member a counselor has replied:
     * the badge only appeared once they had already looked.
     *
     * No polling. The service reads the feed each time the member's token
     * lands or changes, not on the first authenticated emission: a sign-in
     * without a reload turns the session authenticated while the guest's
     * token is still in place, and a read then came back Forbidden and was
     * never tried again. The drawer's own `load()` on open only asks when
     * nothing has loaded or the last read failed.
     */
    this.notificationService.watchSession();
  }

  /**
   * GR-15. The 3/3 counter opens the CURRENT confirmation dialog.
   *
   * It opened `GoalDialogComponent`, the legacy one the team migrated away from
   * when `cpd-goal-set-dialog` was written, and never repointed -- so a member
   * pressing the counter got a different dialog from the one they had just been
   * shown when they set a goal. Two confirmations for the same four goals,
   * disagreeing about what they looked like.
   *
   * Raised through the celebration service rather than opened here, because
   * that service already owns "the goal dialog is showing" for the two places
   * that set a goal. A third mechanism in the header would be a third thing to
   * keep in step.
   */
  public openGoalDialog(anyGoals: boolean): void {
    if (anyGoals) {
      this.goalCelebration.review();
    }
  }

  /** GR-15. The checklist, from the same builder the schools step uses. */
  protected readonly goalDialogRows$ = this.goalFacade.goals$.pipe(
    map((goals) => goalRows(goals as GoalsForRows))
  );

  /** GR-15. Whether the dialog is on screen, however it was opened. */
  protected readonly goalDialogOpen = this.goalCelebration.isOpen;

  /**
   * CPD-664. True while a page is mounting its own copy of the dialog, in
   * which case the shell must not mount a second one over the top of it.
   * See GoalCelebrationService.hostedByPage.
   */
  protected readonly goalDialogHostedByPage = this.goalCelebration.hostedByPage;

  /** GR-15. Which goal was just set, when that is why it opened. */
  protected readonly goalDialogJustSet = this.goalCelebration.justSet;

  /**
   * CPD-667. Whether the leaving-or-staying question applies.
   *
   * Was `!isCivilian$`, which asked it of every Veteran and every Guard and
   * Reserve member as well. `isActiveDuty$` is null until the profile lands
   * and only ever true for serve type 1, so the question is skipped while we
   * do not know. That is the direction to fail in: a question skipped beats
   * a member held at a gate neither of whose answers is true for them.
   */
  protected readonly isActiveDuty$ = this.userProfileFacade.isActiveDuty$;

  /**
   * CPD-661. The member's answer to "Are you leaving, or staying in?".
   *
   * The dialog takes `intent` as an INPUT and `pickIntent` only emits -- it
   * never mutates its own copy. So a call site that does not bind both
   * `[intent]` and `(intentPicked)` renders a radio group that can never
   * show a selection and a gate that can never be satisfied. That is what
   * the masthead did: the answer went nowhere and the member was stuck.
   *
   * Seeded from the profile so a member who has already answered, anywhere,
   * is not asked again.
   */
  protected readonly serviceIntent = signal<ServiceIntent>('');

  private readonly seedServiceIntent =
    this.favoriteMilitaryOccupationsFacade.userReclassifying$
      .pipe(takeUntilDestroyed())
      .subscribe((reclassifying) => {
        if (reclassifying?.transitionfrommilitary) {
          this.serviceIntent.set('transition');
          return;
        }

        if (reclassifying?.stayincurrent) {
          this.serviceIntent.set('stay');
          // WALK-71: the list the dialog shows under "staying in".
          this.favoriteMilitaryOccupationsFacade.loadMilitaryOccupationsForGoal();
        }
      });

  /**
   * WALK-71. The dialog's related military occupations, the same list and
   * the same saved state the schools step passes (the two hosts are kept
   * alike; see goal-set-dialog.call-sites.spec.ts).
   */
  protected readonly relatedForDialog$: Observable<MilitaryOccupation[]> =
    combineLatest([
      this.favoriteMilitaryOccupationsFacade.militaryOccupationsForGoal$,
      this.favoriteMilitaryOccupationsFacade.favoriteMilitaryOccupations$
    ]).pipe(
      map(([occupations, favorites]) => {
        const saved = (favorites ?? [])
          .filter((occupation) => occupation.isFavorite !== false)
          .map((occupation) => occupation.mocCode);
        return (occupations ?? []).slice(0, 6).map((occupation) => ({
          code: occupation.mocCode,
          title: occupation.mocTitle ?? occupation.mocCode,
          branch: occupation.branchCode ?? '',
          isFavorite: saved.includes(occupation.mocCode)
        }));
      })
    );

  /** WALK-71 review. The list is loading (null in the store). */
  protected readonly relatedLoading$ =
    this.favoriteMilitaryOccupationsFacade.militaryOccupationsForGoal$.pipe(
      map((occupations) => occupations == null)
    );

  /** WALK-70 review. The saved leaving-or-staying answer has been read. */
  protected readonly intentLoaded$ = this.favoriteMilitaryOccupationsFacade.userReclassifyingLoaded$;

  /**
   * WALK-71 review. The list must be the CURRENT goal's: reload it when the
   * dialog opens on "staying in", and when the career goal changes while it
   * is the answer. Without this the masthead copy could show the previous
   * goal's occupations.
   */
  private readonly reloadRelatedOnOpen = effect(() => {
    if (this.goalDialogOpen() && this.serviceIntent() === 'stay') {
      untracked(() => this.favoriteMilitaryOccupationsFacade.loadMilitaryOccupationsForGoal());
    }
  });

  private readonly reloadRelatedOnGoal = this.goalFacade.goals$
    .pipe(
      map((goals) => (goals as { onetCode?: string | null } | null)?.onetCode ?? null),
      distinctUntilChanged(),
      skip(1),
      takeUntilDestroyed()
    )
    .subscribe((onetCode) => {
      if (onetCode && this.serviceIntent() === 'stay') {
        this.favoriteMilitaryOccupationsFacade.loadMilitaryOccupationsForGoal();
      }
    });

  /** WALK-71. The career goal's title, for the list's heading. */
  protected readonly careerTitle$ = this.goalFacade.goals$.pipe(
    map((goals) => (goals as { occupationTitle?: string | null } | null)?.occupationTitle ?? '')
  );

  /** WALK-71. Null until the profile lands, as on the schools step. */
  protected readonly canMatchOccupations$ = this.userProfileFacade.userProfile$.pipe(
    map((profile) => (profile ? !!profile.isSkillsSurveyComplete : null))
  );

  /** WALK-71. Saving, or un-saving, one of them as a goal. */
  protected toggleOccupation(occupation: MilitaryOccupation): void {
    if (occupation.isFavorite) {
      this.favoriteMilitaryOccupationsFacade.removeMilitaryFavoriteOccupation(
        occupation.code,
        occupation.title
      );
      return;
    }
    this.favoriteMilitaryOccupationsFacade.addMilitaryFavoriteOccupation(
      occupation.code,
      occupation.title
    );
  }

  /**
   * Records the answer against the profile as well as locally.
   *
   * Only the picked flag goes true. Writing both false would clear an answer
   * the member had already given.
   */
  protected pickServiceIntent(intent: ServiceIntent): void {
    this.serviceIntent.set(intent);

    if (intent === 'transition') {
      this.favoriteMilitaryOccupationsFacade.updateReclassifying(true, false);
      return;
    }

    if (intent === 'stay') {
      this.favoriteMilitaryOccupationsFacade.updateReclassifying(false, true);
      this.favoriteMilitaryOccupationsFacade.loadMilitaryOccupationsForGoal();
    }
  }

  /**
   * CPD-661. Remove a goal from the masthead dialog.
   *
   * The dialog emitted `goalRemoved` and the masthead listened for nothing,
   * so Remove was a control that did visibly nothing. The same dialog opened
   * from the schools step removed goals correctly, which is what made this
   * look intermittent rather than absent.
   *
   * Through GoalRemovalService, so it confirms first; focus then goes back
   * into the dialog rather than to the page.
   */
  protected removeGoal(key: GoalKey): void {
    let removal: Observable<boolean> | undefined;
    switch (key) {
      case 'career':
        removal = this.goalRemoval.removeCareerGoal();
        break;
      case 'program':
        removal = this.goalRemoval.removeAcademicProgramGoal(null);
        break;
      case 'school':
        removal = this.goalRemoval.removeSchoolGoal();
        break;
      case 'licensure':
        removal = this.goalRemoval.removeLicensureGoal();
        break;
      /*
       * CPD-661 item 5. This case did not exist, in EITHER call site, so
       * Remove on the credential row was a button that did nothing at all.
       * The row was added by CRED.1 and the two switches were never widened
       * with it.
       *
       * `setCredentialGoal(null, null)` also clears the legacy certification
       * id -- see the effect -- because the row shows whichever of the two is
       * set, so clearing only the Registry credential would leave a member
       * pressing Remove on a row that never emptied.
       */
      case 'credential':
        removal = this.goalRemoval.removeCredentialGoal();
        break;
    }
    removal
      ?.pipe(take(1))
      .subscribe((removed) =>
        this.goalSetDialog?.focusAfterRemoval(key, removed)
      );
  }

  protected closeGoalDialog(): void {
    this.goalCelebration.dismiss();
  }

  /**
   * CPD-661. Continue goes to the step the member has NOT done yet.
   *
   * Both `continued` and `reportOpened` were bound to one handler that
   * always went to the Goals Report. So at 1/3 and 2/3 the button said
   * "Explore and set a program goal" or "Choose a school" and then went
   * somewhere else entirely -- the dialog's own `primaryLabel` was telling
   * the truth and the masthead was not.
   *
   * The destination is the schools-and-programs step, which is a section of
   * the occupation detail page for the member's career goal rather than a
   * page of its own. The scroll is handed over the way the legacy dialog
   * did it: the occupation page reads this key on arrival.
   *
   * With no career goal there is no occupation page to land on, so this
   * sends them to pick one, which is the actual next step in that case.
   */
  protected continueFromGoalDialog(): void {
    this.goalFacade.goals$.pipe(take(1)).subscribe((goals) => {
      const onetCode = (goals as { onetCode?: string | null })?.onetCode;
      this.closeGoalDialog();

      // GR.6. The Skills Survey's goals step, not the legacy wizard.
      if (!onetCode) {
        this.navigationService.goToSkillsSurveyGoalsStep();
        return;
      }

      /*
       * CPD-661 item 6. ALREADY THERE IS NOT A NAVIGATION.
       *
       * The scroll is handed over in session storage and read once, in the
       * occupation page's `ngAfterViewInit`. A member standing on the
       * occupation page of their own career goal is asked to navigate to the
       * URL they are already on, so the router does nothing, the component is
       * not re-created, and nothing ever reads the key back -- the dialog
       * closed and the page sat still. The key was also left behind to fire
       * on some later visit.
       *
       * So when the destination is the current page, scroll it directly. The
       * legacy dialog did exactly this, by element id, which is why this bug
       * only appeared once the counter started raising the new one.
       */
      if (this.isOnOccupationPage(onetCode)) {
        this.scrollToSchools();
        return;
      }

      sessionStorage.setItem(STORAGE_KEY.SCROLL, SCROLL_TO.INSTITUTION);
      this.navigationService.goToOccupationDetailPageAlt(
        onetCode,
        (goals as { jobId?: number | null })?.jobId ?? null,
        'buildYourTarget'
      );
    });
  }

  /** Whether the browser is already on this occupation's detail page. */
  private isOnOccupationPage(onetCode: string): boolean {
    return this.router.url.split('?')[0].includes(`/${onetCode}`);
  }

  /**
   * After the dialog is gone, so the scroll lands on the settled layout. The
   * section is found by id rather than by a ViewChild because it belongs to
   * the routed page, not to the shell this dialog is mounted in.
   */
  private scrollToSchools(): void {
    setTimeout(() => {
      document
        .getElementById(SCHOOLS_EL_ID)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, GOAL_DIALOG_SCROLL_DELAY_MS);
  }

  /** CPD-661. "Open your Goals Report", which is the 3/3 case only. */
  protected openGoalsReportFromDialog(): void {
    this.closeGoalDialog();
    this.navigationService.goToGoalsReport();
  }

  /**
   * "YOUR GOALS REPORT" GOES TO THE GOALS REPORT.
   *
   * It used to have a third branch: with goals set but the leaving-or-staying
   * question unanswered, it opened the LEGACY goal dialog instead of
   * navigating. That is the "I get the old goals report and Military
   * Occupations You May Like popup" report, and it fired even for a member who
   * had answered that question, because the new confirmation dialog never
   * persisted the answer. (Fixed separately, in the schools-programs
   * container.)
   *
   * The branch is gone rather than re-pointed. `computeGoalsReportLock` is
   * already the product's stated prerequisite gate and it is checked first, so
   * a member who is genuinely not ready is sent to the prerequisite they are
   * missing. A SECOND gate in the masthead, asking a question that the new
   * confirmation dialog now asks at the moment a goal is set, is a leftover of
   * the old flow: it turned a navigation control into a quiz.
   *
   * `anyFavoritesMilitary` is no longer read here. The parameter stays so the
   * three call sites in the template keep their shape.
   */
  public openGoalReportPage(
    anyFavoritesMilitary: boolean,
    anyGoals: boolean,
    lock?: GoalsReportLock | null
  ): void {
    // BUG-590: the button stays clickable while locked; clicking takes the
    // user to whichever prerequisite they are missing instead of dead-ending.
    if (lock?.locked) {
      this.goToMissingPrerequisite(lock);
      return;
    }

    void anyFavoritesMilitary;

    if (!anyGoals) {
      return;
    }

    /*
     * CPD-660. The question is required again here, and this is a REVERSAL
     * of the removal argued for directly above. QA found the two ways into
     * the report disagreeing: from the goals dialog a military member had to
     * answer, from this button they did not. Product chose to make the
     * answer required on both, so the report is never built without it.
     *
     * It is not a second bespoke gate, which is what the old branch was and
     * what the comment above rightly objected to. It raises the SAME dialog
     * that asks the question everywhere else, already answered-aware and
     * already seeded from the profile, so a member who has answered is never
     * stopped and a member who has not is asked once, in one place.
     *
     * Active duty only, per CPD-667: nobody else has an answer to give.
     */
    this.isActiveDuty$.pipe(take(1)).subscribe((isActiveDuty) => {
      if (isActiveDuty && !this.serviceIntent()) {
        this.goalCelebration.review();
        return;
      }

      this.navigationService.goToGoalsReport();
    });
  }

  /**
   * GR.6. Both prerequisites are answered in the Skills Survey.
   *
   * Survey finished, goal missing: the survey's goals step, where a career goal
   * is picked. This went to /career-planning/build-your-target/career-goals,
   * the legacy wizard, which the product no longer uses.
   *
   * Survey unfinished (with or without a goal): the survey where the member
   * left off. It has to be finished first, and the goals step is on the way.
   */
  public goToMissingPrerequisite(lock: GoalsReportLock): void {
    if (lock.target === 'career-goal') {
      this.navigationService.goToSkillsSurveyGoalsStep();
      return;
    }
    this.navigationService.goToSkillsSurvey();
  }

  public openSavedForLaterDialog(anyFavorites: boolean): void {
    if (anyFavorites) {
      this.dialog.open(SavedForLaterDialogComponent, {
        ariaLabel: 'Saved for later'
      });
    }

    this.accountCreationPromptService.openAccountCreationDialog(
      'saved-for-later'
    );
  }

  public openSaveSessionDialog(): void {
    this.dialog.open(SaveSessionDialogComponent, {
      ariaLabel: 'Save your session'
    });
    this.accountCreationPromptService.openAccountCreationDialog('save-session');
  }

  public logOut(): void {
    this.cognitoAuthFacade.signOut();
  }

  public feedback(): void {
    this.dialog.open(FeedbackDialogComponent, {
      ariaLabel: 'Send feedback',
      width: '610px'
    });
  }

  public toggleNavbar(event: MouseEvent): void {
    event.stopPropagation();
    this.isMenuOpen = !this.isMenuOpen;

    // MOBILE.1 R2. Opened from the bottom bar, the sheet takes focus on its
    // first item, so a keyboard or switch user is in the menu rather than on
    // the button beneath it.
    if (this.isMenuOpen) {
      setTimeout(() =>
        document
          .querySelector<HTMLElement>('#cpd-phone-menu a, #cpd-phone-menu button')
          ?.focus()
      );
    }
  }

  public openLoginDialog(): void {
    this.accountCreationPromptService.openAccountCreationDialog();
  }

  /**
   * PWS 4.3.4. The feed is already loaded by the time the drawer opens (see the
   * constructor); this call is the no-op that keeps the drawer correct if it is
   * ever opened before the session settles. The drawer takes focus so a
   * keyboard user is inside it rather than still on the bell behind a scrim.
   */
  public toggleAlerts(): void {
    this.isAlertsOpen = !this.isAlertsOpen;

    if (this.isAlertsOpen) {
      this.notificationService.load();
    }
  }

  public closeAlerts(): void {
    this.isAlertsOpen = false;
  }

  /**
   * MOBILE-0930. The phone bell. The Menu sheet is a modal that covers the
   * screen, so opening the alerts closes it first: one sheet at a time.
   */
  public togglePhoneAlerts(): void {
    if (this.isMenuOpen) {
      this.isMenuOpen = false;
    }
    this.toggleAlerts();
  }

  /** #9 v3. Account (signed in) or the menu button (signed out) opens it. */
  public toggleAccountMenu(event: MouseEvent): void {
    event.stopPropagation();
    this.isAlertsOpen = false;
    this.accountMenuOpen.set(!this.accountMenuOpen());
  }

  public closeAccountMenu(): void {
    this.accountMenuOpen.set(false);
  }

  @HostListener('document:keydown.escape')
  public closeAccountMenuOnEscape(): void {
    this.accountMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  public closeAccountMenuOnClickAway(event: MouseEvent): void {
    const target = event.target as HTMLElement | null;
    if (this.accountMenuOpen() && !target?.closest?.('.hs-acct')) {
      this.accountMenuOpen.set(false);
    }
  }

  /*
   * #9 v3. THE RAIL'S TOOLS IN THE PHONE MENU. Under 960px the rail is not
   * mounted, so Ask Via, Connect and Upload are also offered in the menu
   * sheet. Each does what its rail button does (sidebar.component.ts):
   * Ask Via opens the chat for a member and the account prompt for a
   * visitor, as cpd-via-button does; Connect and Upload open their panels
   * with the others closed.
   */
  public async openRailTool(tool: 'via' | 'connect' | 'upload'): Promise<void> {
    const chat = this.injector.get(AiChatFacade);
    const jst = this.injector.get(JstSidebarService);
    const ace = this.injector.get(AceSidebarService);
    const connect = this.injector.get(ConnectSidebarService);

    // The rail's gate (sidebar toggleConnect): Connect needs the skills
    // survey and a career goal. Said in the sheet, which stays open, rather
    // than opening a panel that has nothing to show.
    if (tool === 'connect') {
      const disabled = await firstValueFrom(
        connect.isConnectItemDisabled$.pipe(
          take(1),
          timeout({ first: RAIL_READ_TIMEOUT_MS, with: () => of(false) })
        )
      );
      this.connectBlocked.set(!!disabled);
      if (disabled) {
        return;
      }
    }

    this.isMenuOpen = false;
    if (tool === 'via') {
      this.isAuthenticated$.pipe(take(1)).subscribe((signedIn) =>
        signedIn
          ? chat.openChatVia()
          : this.accountCreationPromptService.openAccountCreationDialog(
              'skills-survey'
            )
      );
      return;
    }
    chat.closeChatVia();
    if (tool === 'connect') {
      jst.closeJstSidebar();
      ace.closeAceSidebar();
      connect.openConnectSidebar();
      return;
    }
    connect.closeConnectSidebar();
    // The rail reads this flag with an initial value of false; a stream that
    // has not emitted must not leave Upload hanging here either.
    const aceOn = await firstValueFrom(
      this.injector
        .get(FeatureFlagService)
        .isEnabled$('ACE_TRANSCRIPT_UPLOAD_ENABLED')
        .pipe(
          take(1),
          timeout({ first: RAIL_READ_TIMEOUT_MS, with: () => of(false) })
        )
    );
    if ((await jst.resolveUploadEntry(!!aceOn)) === 'picker') {
      ace.resetFlow();
      await ace.openAceSidebar();
      return;
    }
    await jst.openJstSidebar();
  }

}
